class DrvTestimonials extends HTMLElement {
  #x = 0;
  #vel = 0;
  #dragging = false;
  #lastPointerX = 0;
  #lastT = 0;
  #raf = null;
  #half = 0;
  #visible = false;
  #io = null;
  #reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  connectedCallback() {
    this.track = this.querySelector('[data-drv-track]');
    if (!this.track) return;
    this.#clone();
    this.addEventListener('pointerdown', this.#down);
    this.addEventListener('dragstart', this.#noNativeDrag);
    this.addEventListener('wheel', this.#wheel, { passive: false });
    addEventListener('pointermove', this.#move);
    addEventListener('pointerup', this.#up);
    addEventListener('pointercancel', this.#up);
    addEventListener('blur', this.#up);
    const section = this.closest('.drv-t, .drv-rxl');
    section?.querySelector('[data-drv-prev]')?.addEventListener('click', () => this.#nudge(1));
    section?.querySelector('[data-drv-next]')?.addEventListener('click', () => this.#nudge(-1));
    this.#io = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      cancelAnimationFrame(this.#raf);
      if (!this.#visible) return;
      this.#lastT = performance.now();
      this.#raf = requestAnimationFrame(this.#tick);
    });
    this.#io.observe(this);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#raf);
    this.#io?.disconnect();
    removeEventListener('pointermove', this.#move);
    removeEventListener('pointerup', this.#up);
    removeEventListener('pointercancel', this.#up);
    removeEventListener('blur', this.#up);
  }

  #noNativeDrag = (e) => {
    e.preventDefault();
  };

  #clone() {
    const originals = [...this.track.children];
    if (!originals.length) return;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const setW = this.track.scrollWidth + gap;
    const copies = setW > gap ? Math.max(1, Math.ceil((gap + this.clientWidth * 2) / setW)) : 1;
    const frag = document.createDocumentFragment();
    for (let i = 0; i < copies; i++) {
      for (const el of originals) {
        const c = el.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.inert = true;
        frag.appendChild(c);
      }
    }
    this.track.appendChild(frag);
    this.#half = setW;
  }

  get #speed() {
    return this.#reduced ? 0 : parseFloat(this.dataset.speed || '30');
  }

  #tick = (t) => {
    const dt = Math.min((t - this.#lastT) / 1000, 0.05);
    this.#lastT = t;
    if (!this.#dragging) {
      const auto = -this.#speed;
      this.#vel += (auto - this.#vel) * Math.min(1, dt * 2);
      this.#x += this.#vel * dt;
    }
    const half = this.#half || this.track.scrollWidth / 2;
    if (this.#x <= -half) this.#x += half;
    if (this.#x > 0) this.#x -= half;
    this.track.style.transform = 'translate3d(' + this.#x + 'px, 0, 0)';
    if (this.#visible) this.#raf = requestAnimationFrame(this.#tick);
  };

  #down = (e) => {
    this.#dragging = true;
    this.classList.add('is-dragging');
    this.#lastPointerX = e.clientX;
    this.#vel = 0;
  };

  #move = (e) => {
    if (!this.#dragging) return;
    if (e.pointerType === 'mouse' && e.buttons === 0) {
      this.#up();
      return;
    }
    const dx = e.clientX - this.#lastPointerX;
    this.#lastPointerX = e.clientX;
    this.#x += dx;
    this.#vel = dx * 60;
  };

  #wheel = (e) => {
    if (e.ctrlKey || Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
    e.preventDefault();
    this.#x -= e.deltaMode === 1 ? e.deltaX * 16 : e.deltaX;
    this.#vel = 0;
  };

  #up = () => {
    if (!this.#dragging) return;
    this.#dragging = false;
    this.classList.remove('is-dragging');
    this.#vel = Math.max(-800, Math.min(800, this.#vel));
  };

  #nudge(dir) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const step = card.getBoundingClientRect().width + 16;
    this.#vel = dir * step * 2.5;
  }
}

if (!customElements.get('drv-testimonials')) {
  customElements.define('drv-testimonials', DrvTestimonials);
}
